import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, CalendarClock, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

export default function Schedule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await api.get("/schedules");
      if (alive) {
        setItems(data);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const update = async (key, patch) => {
    setSavingKey(key);
    const item = items.find((i) => i.key === key);
    try {
      await api.put(`/schedules/${key}`, {
        enabled: patch.enabled ?? item.enabled,
        time_of_day: patch.time_of_day ?? item.time_of_day,
        categories: patch.categories ?? item.categories,
      });
      toast.success("Jadwal diperbarui");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="schedule-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Jadwal Reminder</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Atur kapan reminder pembayaran otomatis dikirim ke pelanggan via WhatsApp.
        </p>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden divide-y divide-[#E2E8F0]">
        {items.map((s) => (
          <div key={s.key} className="px-6 py-5 flex items-center justify-between gap-4" data-testid={`schedule-row-${s.key}`}>
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-md bg-[#EFF6FF] flex items-center justify-center shrink-0">
                <CalendarClock className="w-4 h-4 text-[#0066FF]" strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0F172A]">{s.label}</div>
                <div className="text-xs text-[#64748B] font-mono">{s.key}</div>
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#64748B]">Kategori:</span>
                <input
                  type="text"
                  placeholder="Semua"
                  defaultValue={s.categories}
                  onBlur={(e) => {
                    if (e.target.value !== s.categories) update(s.key, { categories: e.target.value });
                  }}
                  className="bg-white border border-[#E2E8F0] rounded-md px-2 py-1 text-xs focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none w-28"
                  title="Filter nama pelanggan atau barang. Contoh: 'Tiket'. Bisa pakai '!' untuk kecualikan, misal: 'Tiket, !Umroh'"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#64748B]">Jam:</span>
                <input
                  type="time"
                  defaultValue={s.time_of_day}
                  onBlur={(e) => {
                    if (e.target.value !== s.time_of_day) update(s.key, { time_of_day: e.target.value });
                  }}
                  data-testid={`schedule-time-${s.key}`}
                  className="bg-white border border-[#E2E8F0] rounded-md px-2 py-1 text-xs focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
                />
              </div>
              <Switch
                checked={s.enabled}
                onCheckedChange={(v) => update(s.key, { enabled: v })}
                data-testid={`schedule-toggle-${s.key}`}
              />
              {savingKey === s.key && <Loader2 className="w-4 h-4 animate-spin text-[#64748B]" />}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-[#64748B] bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-4 space-y-3">
        <p>
          <strong className="text-[#0F172A]">Catatan Pengiriman:</strong> Background scheduler memeriksa invoice setiap 5 menit
          dan mengirim reminder otomatis sesuai jadwal aktif. Reminder yang sudah dikirim hari yang sama tidak akan dikirim ulang.
        </p>
        <p>
          <strong className="text-[#0F172A]">Panduan Filter Kategori:</strong> Anda dapat memfilter target berdasarkan Kategori Pelanggan atau Nama Barang. Pisahkan dengan koma jika lebih dari satu (misal: <code className="bg-white border rounded px-1">Tiket, Visa</code>).
        </p>
        <p>
          <strong className="text-[#0F172A]">Pengecualian (Tanda !):</strong> Gunakan tanda seru <code>!</code> di awal kata untuk MENCEGAH pengiriman. <br/>Contoh: <code className="bg-white border rounded px-1 text-red-600 font-medium">Tiket, !Umroh, !Haji</code> artinya sistem akan mengirim pengingat ke tagihan Tiket, namun akan <b>membatalkan pengiriman</b> jika di dalam tagihan tersebut ada item Umroh atau Haji.
        </p>
      </div>
    </div>
  );
}
